import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Phone, Calendar, User, Building2, Clock, FileText, CheckCircle2, ExternalLink, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/common/Dialog';
import { accountsService } from '@/services/accounts.service';
import { contactsService } from '@/services/contacts.service';

interface Call {
  id: string;
  accountName: string;
  contactName: string;
  accountId?: string;
  contactId?: string;
  scheduledDate: string;
  duration: number;
  status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'RESCHEDULED';
  type: 'DISCOVERY' | 'DEMO' | 'FOLLOW_UP' | 'CLOSING';
  notes: string;
  owner: string;
}

interface Account {
  id: string;
  name: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  name?: string;
  accountId?: string;
  account_id?: string;
}

const statusColors = {
  SCHEDULED: 'default',
  COMPLETED: 'default',
  CANCELLED: 'destructive',
  RESCHEDULED: 'secondary',
} as const;

const typeColors = {
  DISCOVERY: 'outline',
  DEMO: 'default',
  FOLLOW_UP: 'secondary',
  CLOSING: 'default',
} as const;

export default function CallDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [call, setCall] = useState<Call | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  
  // Database state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  
  const [editForm, setEditForm] = useState({
    accountId: '',
    accountName: '',
    contactId: '',
    contactName: '',
    scheduledDate: '',
    duration: 60,
    status: 'SCHEDULED' as Call['status'],
    type: 'DISCOVERY' as Call['type'],
    notes: '',
  });

  // Load accounts and contacts from DB
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        console.log('📊 Loading accounts and contacts for Call Detail...');
        
        // Load Accounts fresh (cover all, not a stale/capped cache)
        const accountsResponse = await accountsService.getAccounts({ limit: 5000 });
        setAccounts(accountsResponse.data);

        // Global contacts load is only a fallback; per-account fetch drives the dropdown.
        const contactsResponse = await contactsService.getContacts({ limit: 1000 });
        const transformedContacts = contactsResponse.data.map(c => ({
          ...c,
          name: `${c.firstName} ${c.lastName}`,
          accountId: c.accountId || c.account_id,
        }));
        setContacts(transformedContacts);
      } catch (error) {
        console.error('❌ Error loading data:', error);
      } finally {
        setLoadingData(false);
      }
    };
    
    loadData();
  }, []);

  // Load the selected account's contacts directly (avoids the capped global list).
  useEffect(() => {
    if (!editForm.accountId) { setFilteredContacts([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await contactsService.getContacts({ account_id: editForm.accountId, limit: 500 });
        const mapped = res.data.map((c: any) => ({ ...c, name: c.name || `${c.firstName} ${c.lastName}`.trim(), accountId: c.accountId || c.account_id }));
        if (!cancelled) setFilteredContacts(mapped);
      } catch {
        if (!cancelled) setFilteredContacts(contacts.filter(c => c.accountId === editForm.accountId));
      }
    })();
    return () => { cancelled = true; };
  }, [editForm.accountId]);

  useEffect(() => {
    if (!id || accounts.length === 0 || contacts.length === 0) return;

    const fetchCall = async () => {
      try {
        setLoading(true);
        
        // Fetch call from API
        const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        
        if (response.ok) {
          const task = await response.json();
          
          // Transform backend data to frontend format
          const foundCall: Call = {
            id: task.id,
            accountName: task.contact?.account?.name || 'Unknown Account',
            contactName: task.contact 
              ? `${task.contact.firstName} ${task.contact.lastName}`
              : 'Unknown Contact',
            accountId: task.contact?.account?.id || '',
            contactId: task.contactId || '',
            scheduledDate: task.dueDate || new Date().toISOString(),
            duration: task.callPackData?.duration || 60,
            status: task.status === 'OPEN' ? 'SCHEDULED' : task.status === 'DONE' ? 'COMPLETED' : task.status === 'SKIPPED' ? 'CANCELLED' : 'SCHEDULED',
            type: task.callType === 'CALL1' ? 'DISCOVERY' : task.callType === 'CALL2' ? 'DEMO' : 'FOLLOW_UP',
            notes: task.callPackData?.notes || '',
            owner: task.assignedTo?.name || 'Unassigned',
          };
          
          setCall(foundCall);
          
          // Set edit form
          setEditForm({
            accountId: foundCall.accountId,
            accountName: foundCall.accountName,
            contactId: foundCall.contactId,
            contactName: foundCall.contactName,
            scheduledDate: foundCall.scheduledDate.slice(0, 16),
            duration: foundCall.duration,
            status: foundCall.status,
            type: foundCall.type,
            notes: foundCall.notes,
          });
          
          console.log('📝 Loaded call from API:', foundCall);
        } else {
          addToast({ type: 'error', title: 'Call Not Found', description: 'The call could not be found.' });
          navigate('/calls');
        }
      } catch (error) {
        console.error('Error loading call:', error);
        addToast({ type: 'error', title: 'Error', description: 'Failed to load call details' });
      } finally {
        setLoading(false);
      }
    };

    fetchCall();
  }, [id, accounts, contacts]);

  const handleSave = async () => {
    if (!call) return;

    try {
      // Update call via API
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks/${call.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          contactId: editForm.contactId,
          callType: editForm.type === 'DISCOVERY' ? 'CALL1' : editForm.type === 'DEMO' ? 'CALL2' : 'CALL3',
          dueDate: new Date(editForm.scheduledDate).toISOString(),
          status: editForm.status === 'SCHEDULED' ? 'OPEN' : editForm.status === 'COMPLETED' ? 'DONE' : 'SKIPPED',
          callPackData: {
            type: editForm.type,
            duration: editForm.duration,
            notes: editForm.notes,
          },
        }),
      });

      if (response.ok) {
        setCall({ ...call, ...editForm, scheduledDate: new Date(editForm.scheduledDate).toISOString() });
        setIsEditMode(false);
        addToast({ type: 'success', title: 'Call Updated', description: 'Call has been updated successfully.' });
      } else {
        throw new Error('Failed to update call');
      }
    } catch (error) {
      console.error('Error saving call:', error);
      addToast({ type: 'error', title: 'Error', description: 'Failed to save changes' });
    }
  };

  const handleDelete = async () => {
    if (!call) return;

    try {
      // Delete call via API
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks/${call.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.ok) {
        addToast({ type: 'success', title: 'Call Deleted', description: 'Call has been deleted successfully.' });
        navigate('/calls');
      } else {
        throw new Error('Failed to delete call');
      }
    } catch (error) {
      console.error('Error deleting call:', error);
      addToast({ type: 'error', title: 'Error', description: 'Failed to delete call' });
    }
  };

  const handleReschedule = async () => {
    if (!call || !rescheduleDate) return;

    try {
      // Update call via API with new date
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks/${call.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          dueDate: new Date(rescheduleDate).toISOString(),
          status: 'OPEN', // Keep as OPEN when rescheduling
        }),
      });

      if (response.ok) {
        setCall({ ...call, scheduledDate: new Date(rescheduleDate).toISOString(), status: 'RESCHEDULED' });
        setRescheduleDialogOpen(false);
        setRescheduleDate('');
        addToast({ type: 'success', title: 'Call Rescheduled', description: 'Call has been rescheduled successfully.' });
      } else {
        throw new Error('Failed to reschedule call');
      }
    } catch (error) {
      console.error('Error rescheduling call:', error);
      addToast({ type: 'error', title: 'Error', description: 'Failed to reschedule call' });
    }
  };

  const handleMarkComplete = async () => {
    if (!call) return;

    try {
      // Mark call as complete via API
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks/${call.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          status: 'DONE', // Backend uses DONE for completed
        }),
      });

      if (response.ok) {
        setCall({ ...call, status: 'COMPLETED' });
        addToast({ type: 'success', title: 'Call Completed', description: 'Call marked as completed.' });
      } else {
        throw new Error('Failed to mark call as complete');
      }
    } catch (error) {
      console.error('Error marking complete:', error);
      addToast({ type: 'error', title: 'Error', description: 'Failed to mark call as complete' });
    }
  };

  const handleDuplicateCall = () => {
    if (!call) return;

    try {
      const savedCalls = localStorage.getItem('calls');
      if (savedCalls) {
        const calls: Call[] = JSON.parse(savedCalls);
        const newCall: Call = {
          ...call,
          id: `call-${Date.now()}`,
          status: 'SCHEDULED',
          notes: call.notes + ' (Duplicated)',
        };
        const updated = [...calls, newCall];
        localStorage.setItem('calls', JSON.stringify(updated));
        addToast({ type: 'success', title: 'Call Duplicated', description: 'New call created from this call.' });
        navigate(`/calls/${newCall.id}`);
      }
    } catch (error) {
      console.error('Error duplicating call:', error);
      addToast({ type: 'error', title: 'Error', description: 'Failed to duplicate call' });
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!call) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        onBack={() => navigate('/calls')}
        title="Call Details"
        actions={
          <>
            {!isEditMode && (
              <>
                <Button variant="outline" onClick={() => setIsEditMode(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            {isEditMode && (
              <>
                <Button variant="outline" onClick={() => setIsEditMode(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  Save Changes
                </Button>
              </>
            )}
          </>
        }
      />

      {/* Main Content */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Call Details Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Call Details</span>
              <div className="flex gap-2">
                <Badge variant={typeColors[call.type]}>{call.type.replace('_', ' ')}</Badge>
                <Badge variant={statusColors[call.status]}>{call.status}</Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isEditMode ? (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Account</p>
                      <p className="text-base font-semibold">{call.accountName}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Contact</p>
                      <p className="text-base font-semibold">{call.contactName}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Scheduled</p>
                      <p className="text-base">{formatDateTime(call.scheduledDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Duration</p>
                      <p className="text-base">{call.duration} minutes</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Owner</p>
                      <p className="text-base">{call.owner}</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Notes</p>
                      <p className="text-base whitespace-pre-wrap">{call.notes || 'No notes'}</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                {loadingData && <p className="text-sm text-muted-foreground">Loading accounts and contacts...</p>}
                
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="accountId">Account Name</Label>
                    <select
                      id="accountId"
                      value={editForm.accountId}
                      onChange={(e) => {
                        const selectedAccount = accounts.find(a => a.id === e.target.value);
                        setEditForm({
                          ...editForm,
                          accountId: e.target.value,
                          accountName: selectedAccount?.name || '',
                          contactId: '',
                          contactName: ''
                        });
                        console.log('📝 Selected account:', selectedAccount?.name);
                      }}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="">Select Account...</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="contactId">Contact Name</Label>
                    <select
                      id="contactId"
                      value={editForm.contactId}
                      onChange={(e) => {
                        const selectedContact = filteredContacts.find(c => c.id === e.target.value);
                        setEditForm({
                          ...editForm,
                          contactId: e.target.value,
                          contactName: selectedContact?.name || ''
                        });
                        console.log('📝 Selected contact:', selectedContact?.name);
                      }}
                      disabled={!editForm.accountId}
                      className="w-full px-3 py-2 border rounded-md disabled:bg-muted disabled:cursor-not-allowed"
                    >
                      <option value="">Select Contact...</option>
                      {filteredContacts.map(contact => (
                        <option key={contact.id} value={contact.id}>
                          {contact.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Scheduled Date & Time</Label>
                    <Input type="datetime-local" value={editForm.scheduledDate} onChange={(e) => setEditForm({ ...editForm, scheduledDate: e.target.value })} />
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input type="number" value={editForm.duration} onChange={(e) => setEditForm({ ...editForm, duration: parseInt(e.target.value) })} />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value as Call['type'] })} className="w-full px-3 py-2 border rounded-md">
                      <option value="DISCOVERY">Discovery</option>
                      <option value="DEMO">Demo</option>
                      <option value="FOLLOW_UP">Follow Up</option>
                      <option value="CLOSING">Closing</option>
                    </select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as Call['status'] })} className="w-full px-3 py-2 border rounded-md">
                      <option value="SCHEDULED">Scheduled</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="RESCHEDULED">Rescheduled</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {call.status !== 'COMPLETED' && (
              <>
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => {
                    setRescheduleDate(call.scheduledDate.slice(0, 16));
                    setRescheduleDialogOpen(true);
                  }}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Reschedule
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-green-600 hover:text-green-700 hover:bg-green-50" 
                  onClick={handleMarkComplete}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark as Completed
                </Button>
              </>
            )}
            <Button 
              variant="outline" 
              className="w-full justify-start" 
              onClick={() => navigate(`/accounts`)}
            >
              <Building2 className="h-4 w-4 mr-2" />
              View Account
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start" 
              onClick={() => navigate(`/contacts`)}
            >
              <User className="h-4 w-4 mr-2" />
              View Contact
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start" 
              onClick={handleDuplicateCall}
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate Call
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setRescheduleDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Reschedule Call</DialogTitle>
            <DialogDescription>
              Choose a new date and time for this call with <span className="font-semibold">{call?.contactName}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rescheduleDate">New Date & Time</Label>
            <Input
              id="rescheduleDate"
              type="datetime-local"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReschedule} disabled={!rescheduleDate}>
              Reschedule Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete Call</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this call with <span className="font-semibold">{call.contactName}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
